import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Progress } from './ui/progress';
import { Button } from './ui/button';
import { getDomains, getProgress, getQuizHistory } from '../api';
import { BookOpen, Target, TrendingUp, Clock, Award, Brain, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from '../hooks/use-toast';
import { useAuth } from '../contexts/AuthContext';

const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(null);
  const [quizResults, setQuizResults] = useState([]);
  const [domains, setDomains] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [progressData, quizData, domainsData] = await Promise.all([
        getProgress(),
        getQuizHistory(),
        getDomains()
      ]);
      
      setProgress(progressData);
      setQuizResults(quizData.results || []);
      setDomains(domainsData);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast({
        title: 'Error loading data',
        description: 'Failed to load dashboard. Please refresh.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!progress) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center">
        <Card>
          <CardHeader>
            <CardTitle>Error</CardTitle>
            <CardDescription>Failed to load data</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={fetchData}>Retry</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const masteryPercentage = Math.round((progress.mastered_items / progress.total_items) * 100);
  const avgQuizScore = quizResults.length > 0
    ? Math.round(quizResults.reduce((acc, quiz) => acc + quiz.score, 0) / quizResults.length)
    : 0;

  const dueForReview = Object.values(progress.item_progress).filter(
    item => item.next_review && new Date(item.next_review) <= new Date()
  ).length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <div className="max-w-7xl mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold text-slate-900">BACB Study Buddy</h1>
              <p className="text-lg text-slate-600">Master the Behavior Analyst Certification Board Task List with confidence</p>
            </div>
            <a 
              href="https://www.pearsonvue.com/us/en/bacb.html" 
              target="_blank" 
              rel="noopener noreferrer"
            >
              <Button 
                className="bg-orange-600 hover:bg-orange-700 transition-colors duration-200"
                size="lg"
              >
                Register for the Board
              </Button>
            </a>
          </div>
        </div>

        {/* Welcome Section */}
        <Card className="border-l-4 border-l-blue-600 bg-gradient-to-r from-blue-50 to-indigo-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="bg-blue-600 text-white rounded-full p-3">
                <Brain className="h-6 w-6" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-slate-900">
                  Welcome back, {user?.first_name}! 👋
                </h2>
                <p className="text-slate-600 mt-1">
                  Ready to continue your study journey? Let's master those concepts!
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="border-l-4 border-l-blue-500 hover:shadow-lg transition-shadow duration-300">
            <CardHeader className="pb-3">
              <CardDescription className="text-slate-600">Overall Progress</CardDescription>
              <CardTitle className="text-3xl font-bold text-blue-600">{masteryPercentage}%</CardTitle>
            </CardHeader>
            <CardContent>
              <Progress value={masteryPercentage} className="h-2" />
              <p className="text-sm text-slate-500 mt-2">{progress.mastered_items} of {progress.total_items} items mastered</p>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-green-500 hover:shadow-lg transition-shadow duration-300">
            <CardHeader className="pb-3">
              <CardDescription className="text-slate-600">Study Streak</CardDescription>
              <CardTitle className="text-3xl font-bold text-green-600 flex items-center gap-2">
                <Award className="h-8 w-8" />
                {progress.study_streak}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-slate-500">days in a row</p>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-purple-500 hover:shadow-lg transition-shadow duration-300">
            <CardHeader className="pb-3">
              <CardDescription className="text-slate-600">Avg Quiz Score</CardDescription>
              <CardTitle className="text-3xl font-bold text-purple-600">{avgQuizScore}%</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-slate-500">{quizResults.length} quizzes completed</p>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-orange-500 hover:shadow-lg transition-shadow duration-300">
            <CardHeader className="pb-3">
              <CardDescription className="text-slate-600">Study Time</CardDescription>
              <CardTitle className="text-3xl font-bold text-orange-600 flex items-center gap-2">
                <Clock className="h-8 w-8" />
                {Math.round(progress.total_study_time / 60)}h
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-slate-500">{progress.total_study_time % 60}m total time</p>
            </CardContent>
          </Card>
        </div>

        {/* Action Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Due for Review */}
          <Card className="hover:shadow-xl transition-all duration-300">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-xl">
                <Brain className="h-6 w-6 text-blue-600" />
                Ready to Review
              </CardTitle>
              <CardDescription>Items scheduled for review today</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-3xl font-bold text-blue-600">{dueForReview}</span>
                <span className="text-slate-500">items waiting</span>
              </div>
              <Button 
                className="w-full bg-blue-600 hover:bg-blue-700 transition-colors duration-200" 
                size="lg"
                onClick={() => navigate('/flashcards')}
              >
                <BookOpen className="mr-2 h-5 w-5" />
                Start Flashcard Review
              </Button>
            </CardContent>
          </Card>

          {/* Practice Quiz */}
          <Card className="hover:shadow-xl transition-all duration-300">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-xl">
                <Target className="h-6 w-6 text-green-600" />
                Practice Quiz
              </CardTitle>
              <CardDescription>Test your knowledge</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-3xl font-bold text-green-600">{domains.length}</span>
                <span className="text-slate-500">domains available</span>
              </div>
              <Button 
                className="w-full bg-green-600 hover:bg-green-700 transition-colors duration-200" 
                size="lg"
                onClick={() => navigate('/quiz')}
              >
                <Target className="mr-2 h-5 w-5" />
                Take a Quiz
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Domain Progress */}
        <Card className="hover:shadow-lg transition-shadow duration-300">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-6 w-6 text-blue-600" />
              Progress by Domain
            </CardTitle>
            <CardDescription>Your mastery across different areas</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {domains.map(domain => {
              const domainItems = domain.items;
              const masteredInDomain = domainItems.filter(
                item => progress.item_progress[item.code]?.mastery >= 80
              ).length;
              const domainProgress = Math.round((masteredInDomain / domainItems.length) * 100);

              return (
                <div key={domain.id} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-3 h-3 rounded-full" 
                        style={{ backgroundColor: domain.color }}
                      />
                      <span className="font-medium text-slate-700">{domain.name}</span>
                    </div>
                    <span className="text-sm font-semibold" style={{ color: domain.color }}>
                      {domainProgress}%
                    </span>
                  </div>
                  <Progress value={domainProgress} className="h-2" />
                  <p className="text-xs text-slate-500">
                    {masteredInDomain} of {domainItems.length} items mastered
                  </p>
                </div>
              );
            })}
          </CardContent>
        </Card>

        {/* Recent Activity */}
        <Card className="hover:shadow-lg transition-shadow duration-300">
          <CardHeader>
            <CardTitle>Quiz Results</CardTitle>
            <CardDescription>Your last 5 quiz attempts</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {quizResults.slice(0, 5).map((quiz) => {
                const domain = domains.find(d => d.id === quiz.domain);
                return (
                  <div 
                    key={quiz.id} 
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors duration-200"
                  >
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-2 h-12 rounded-full" 
                        style={{ backgroundColor: domain?.color }}
                      />
                      <div>
                        <p className="font-medium text-slate-700">{domain?.name}</p>
                        <p className="text-sm text-slate-500">
                          {new Date(quiz.date).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold" style={{ color: domain?.color }}>
                        {quiz.score}%
                      </p>
                      <p className="text-xs text-slate-500">
                        {quiz.correct_answers}/{quiz.total_questions} correct
                      </p>
                    </div>
                  </div>
                );
              })}
              {quizResults.length === 0 && (
                <p className="text-center text-slate-500 py-4">No quiz results yet. Take your first quiz!</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;
